import { Container, Row, Col, Button, FormGroup, FormLabel, FormControl } from 'react-bootstrap';
import Feedback from 'react-bootstrap/esm/Feedback';
import { TextFormInput, TextAreaFormInput, CountryFormInput } from '@/components';
import { Controller } from 'react-hook-form';
import InquiryFormExtras from '@/components/InquiryFormExtras';
import useSiteInquiryForm from '@/hooks/useSiteInquiryForm';
import { getHomeIcon } from '@/views/home/components/homeIconMap';
import { resolveSiteBuilderIconComponent } from '@/utils/siteBuilderIconMap';

const getPartnerFeatureFallbackIcon = (title = '') => {
  const normalized = String(title || '').toLowerCase();
  if (normalized.includes('support') || normalized.includes('service')) return 'FaHeadset';
  if (normalized.includes('partner') || normalized.includes('trust')) return 'FaHandshake';
  if (normalized.includes('exclusive') || normalized.includes('rate')) return 'FaStar';
  if (normalized.includes('booking') || normalized.includes('bookings')) return 'FaArrowTrendUp';
  if (normalized.includes('promotion') || normalized.includes('promote') || normalized.includes('property')) return 'FaGlobe';
  if (normalized.includes('growth') || normalized.includes('performance')) return 'FaArrowTrendUp';
  if (normalized.includes('team') || normalized.includes('people')) return 'FaUsers';
  return null;
};

const ContactPartnerForm = ({ sectionData }) => {
  const content = sectionData?.content || {};
  const featureTitle = content.feature_title || 'Why Partner With Us?';
  const featureText =
    content.feature_text ||
    'We collaborate with tour operators, groups, hoteliers, and excursion suppliers to deliver reliable service and consistent results.';
  const formTitle = content.form_title || 'Get Started Today';
  const formIntro =
    content.form_intro ||
    'Tell us about your business and how you would like to work together.';
  const submitLabel = content.form_submit_label || 'Submit Application';
  const partnerTypes = Array.isArray(content.partner_types) ? content.partner_types : [];
  const items = Array.isArray(sectionData?.items) ? sectionData.items : [];
  const featureItems = items.filter((item) => item.item_type === 'feature');
  const bulletItems = items.filter((item) => item.item_type === 'bullet');

  const {
    control,
    watch,
    onSubmit,
    status,
    error,
    confirmation,
    submitting,
    resetStatus,
    websiteField,
    setWebsiteField,
    turnstile,
    showTurnstileConfigWarning,
  } = useSiteInquiryForm({
    formType: 'partner',
    action: 'partner_inquiry',
    defaultValues: {
      companyName: '',
      partnerType: '',
      contactName: '',
      email: '',
      phone: '',
      country: '',
      groupSize: '',
      travelDateFrom: '',
      travelDateTo: '',
      propertyName: '',
      propertyLocation: '',
      numberOfRooms: '',
      propertyType: '',
      excursionName: '',
      excursionType: '',
      excursionArea: '',
      transferFleet: '',
      markets: '',
      message: ''
    },
    mapValues: (values) => ({
      company_name: values.companyName,
      partner_type: values.partnerType,
      contact_name: values.contactName,
      email: values.email,
      phone: values.phone,
      country: values.country,
      message: values.message,
      extra: {
        group_size: values.groupSize,
        travel_date_from: values.travelDateFrom,
        travel_date_to: values.travelDateTo,
        property_name: values.propertyName,
        location: values.propertyLocation,
        number_of_rooms: values.numberOfRooms,
        property_type: values.propertyType,
        excursion_name: values.excursionName,
        excursion_type: values.excursionType,
        excursion_area: values.excursionArea,
        transfer_fleet: values.transferFleet,
        markets: values.markets,
      },
    }),
  });

  const selectedPartnerType = watch('partnerType') || '';
  const normalizedPartnerType = selectedPartnerType.toLowerCase();
  const isGroup = normalizedPartnerType.includes('group');
  const isOperator = normalizedPartnerType.includes('tour operator') || normalizedPartnerType.includes('travel agency');
  const isHotelier = normalizedPartnerType.includes('hotel');
  const isExcursion = normalizedPartnerType.includes('excursion');
  const isTransfer = normalizedPartnerType.includes('transfer');

  return (
    <section id="contact_form_partners" className="hoteliers-section contact-partner-section py-5 py-lg-6">
      <Container>
        <Row className="mb-5">
          <Col lg={12} className="text-center">
            <h2 className="sec-title mb-3">{sectionData?.title || 'Join Our Team'}</h2>
            {sectionData?.intro_text && (
              <p className="lead text-muted mb-4">
                <span dangerouslySetInnerHTML={{ __html: sectionData.intro_text }} />
              </p>
            )}

            {featureItems.length > 0 && (
              <Row className="g-4 justify-content-center mb-4">
                {featureItems.map((item) => {
                  if (!(item.is_active === 1 || item.is_active === '1' || item.is_active === true || item.is_active === undefined)) {
                    return null;
                  }
                  const fallbackKey = getPartnerFeatureFallbackIcon(item.title);
                  const Icon = item.IconComponent
                    || resolveSiteBuilderIconComponent(item.icon)
                    || getHomeIcon(item.icon)
                    || resolveSiteBuilderIconComponent(fallbackKey);
                  return (
                    <Col key={item.id} xs={6} md={4} lg={3}>
                      <div className="text-center">
                        <div className="hoteliers-icon-wrapper mb-3">
                          {Icon && <Icon className="hoteliers-icon" />}
                        </div>
                        <h5 className="mb-0">{item.title}</h5>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Col>
        </Row>

        <Row className="align-items-start">
          <Col lg={6}>
            <div className="hoteliers-content">
              <h3 className="h4 fw-semibold mb-3">{featureTitle}</h3>
              <p className="mb-4">{featureText}</p>
              {bulletItems.length > 0 && (
                <ul className="list-unstyled mb-4">
                  {bulletItems.map((bullet) => (
                    <li key={bullet.id} className="mb-3 d-flex align-items-start">
                      <span className="text-primary me-2">✓</span>
                      <span>{bullet.title || bullet.description}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Col>
          <Col lg={6}>
            <div className="hoteliers-form-wrapper">
              <h3 className="h4 fw-semibold mb-2">{formTitle}</h3>
              <p className="text-muted mb-4">{formIntro}</p>
              {status === 'success' ? (
                <InquiryFormExtras
                  status={status}
                  confirmation={confirmation}
                  onReset={resetStatus}
                />
              ) : (
              <form onSubmit={onSubmit} className="hoteliers-contact-form" noValidate>
                <div className="row g-3">
                  <TextFormInput
                    name="companyName"
                    label="Company / Property Name *"
                    containerClass="col-md-6"
                    control={control}
                    rules={{ required: 'Company name is required' }}
                  />
                  <FormGroup className="col-md-6">
                    <FormLabel>Partnership Type *</FormLabel>
                    <Controller
                      name="partnerType"
                      control={control}
                      rules={{ required: 'Partner type is required' }}
                      render={({ field, fieldState }) => (
                        <>
                          <FormControl as="select" {...field} isInvalid={Boolean(fieldState.error?.message)}>
                            <option value="">Select partner type</option>
                            {partnerTypes.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </FormControl>
                          {fieldState.error?.message && (
                            <Feedback type="invalid">{fieldState.error.message}</Feedback>
                          )}
                        </>
                      )}
                    />
                  </FormGroup>
                  <TextFormInput
                    name="contactName"
                    label="Contact Name *"
                    containerClass="col-md-6"
                    control={control}
                    rules={{ required: 'Contact name is required' }}
                  />
                  <TextFormInput
                    name="phone"
                    label="Phone *"
                    containerClass="col-md-6"
                    control={control}
                    rules={{ required: 'Phone is required' }}
                  />
                  <TextFormInput
                    name="email"
                    label="Email *"
                    containerClass="col-md-6"
                    control={control}
                    rules={{
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    }}
                  />
                  <CountryFormInput
                    name="country"
                    label="Country"
                    containerClass="col-md-6"
                    control={control}
                  />
                  {(isGroup || isOperator || isHotelier || isExcursion || isTransfer) && (
                    <Col xs={12}>
                      <div className="contact-partner-section__divider" />
                    </Col>
                  )}

                  {isGroup && (
                    <>
                      <Col xs={12}>
                        <h6 className="contact-partner-section__subtitle mb-2">Group details</h6>
                      </Col>
                      <TextFormInput
                        name="groupSize"
                        label="Number of people"
                        containerClass="col-md-6"
                        control={control}
                        type="number"
                      />
                      <TextFormInput
                        name="travelDateFrom"
                        label="Preferred arrival date"
                        containerClass="col-md-6"
                        control={control}
                        type="date"
                      />
                      <TextFormInput
                        name="travelDateTo"
                        label="Preferred departure date"
                        containerClass="col-md-6"
                        control={control}
                        type="date"
                      />
                      <TextFormInput
                        name="markets"
                        label="Origin market / country"
                        containerClass="col-md-6"
                        control={control}
                      />
                    </>
                  )}

                  {isOperator && (
                    <>
                      <Col xs={12}>
                        <h6 className="contact-partner-section__subtitle mb-2">Operator details</h6>
                      </Col>
                      <TextFormInput
                        name="markets"
                        label="Main source markets"
                        containerClass="col-md-12"
                        control={control}
                      />
                    </>
                  )}

                  {isHotelier && (
                    <>
                      <Col xs={12}>
                        <h6 className="contact-partner-section__subtitle mb-2">Property details</h6>
                      </Col>
                      <TextFormInput
                        name="propertyName"
                        label="Property name"
                        containerClass="col-md-6"
                        control={control}
                      />
                      <TextFormInput
                        name="propertyLocation"
                        label="Property location"
                        containerClass="col-md-6"
                        control={control}
                      />
                      <TextFormInput
                        name="numberOfRooms"
                        label="Number of rooms / units"
                        containerClass="col-md-6"
                        control={control}
                        type="number"
                      />
                      <TextFormInput
                        name="propertyType"
                        label="Property type (hotel, villa, apartments)"
                        containerClass="col-md-6"
                        control={control}
                      />
                    </>
                  )}

                  {isExcursion && (
                    <>
                      <Col xs={12}>
                        <h6 className="contact-partner-section__subtitle mb-2">Excursion details</h6>
                      </Col>
                      <TextFormInput
                        name="excursionName"
                        label="Excursion / tour name"
                        containerClass="col-md-6"
                        control={control}
                      />
                      <TextFormInput
                        name="excursionType"
                        label="Tour type (boat, cultural, adventure)"
                        containerClass="col-md-6"
                        control={control}
                      />
                      <TextFormInput
                        name="excursionArea"
                        label="Operating area"
                        containerClass="col-md-12"
                        control={control}
                      />
                    </>
                  )}

                  {isTransfer && (
                    <>
                      <Col xs={12}>
                        <h6 className="contact-partner-section__subtitle mb-2">Transfer details</h6>
                      </Col>
                      <TextFormInput
                        name="transferFleet"
                        label="Fleet size / vehicle types"
                        containerClass="col-md-12"
                        control={control}
                      />
                    </>
                  )}
                  <TextAreaFormInput
                    name="message"
                    label="Additional Information"
                    containerClass="col-12"
                    rows={4}
                    control={control}
                    placeholder="Share details about your business, offerings, and goals."
                  />
                  <Col xs={12}>
                    <InquiryFormExtras
                      honeypotId="partner-website-field"
                      websiteField={websiteField}
                      setWebsiteField={setWebsiteField}
                      turnstileRef={turnstile.containerRef}
                      showTurnstileConfigWarning={showTurnstileConfigWarning}
                      error={error}
                      status={status}
                    />
                  </Col>
                  <Col xs={12}>
                    <Button variant="primary" type="submit" className="w-100 btn-lg" disabled={submitting}>
                      {submitting ? 'Sending…' : submitLabel}
                    </Button>
                  </Col>
                </div>
              </form>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default ContactPartnerForm;
